import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

/**
 * Toggle Group built on Radix, with two appearances:
 *
 * - `segmented` (default) — the dashboard's tabs treatment
 *   (apps/schedugram-client/react-src/components/ui/tabs.tsx, the style-guide
 *   "Tabs" pattern): a grey pill container, white selected chip, purple
 *   (`blue-500` = #4F31FF here) icon + black text on the selected item.
 * - `nav` (SKE-3806) — the platform's primary-nav treatment from the dashboard
 *   sidebar `MenuItem` (react-src/pages/component/menu/menu.tsx): a grey
 *   container with a WHITE selected chip + subtle shadow (`[&.active]:bg-white`
 *   + shadow in menu.tsx), purple (`blue-500` = #4F31FF) icon + near-black text
 *   on the selected item, grey icon + grey text otherwise. Used by the
 *   external-review Navbar view switcher.
 *
 * external-review uses toggle-group semantics (single-select view switcher)
 * rather than tab panels, so this is the toggle-group equivalent of the
 * client's tabs/nav.
 */
const toggleGroupVariants = cva('inline-flex items-center', {
  variants: {
    size: {
      md: 'h-10',
      sm: 'h-8',
    },
    appearance: {
      segmented: 'gap-1 rounded-lg bg-[#F4F4F6] p-1',
      nav: 'gap-1 rounded-lg bg-[#F4F4F6] p-1',
    },
  },
  defaultVariants: {
    size: 'md',
    appearance: 'segmented',
  },
})

const toggleGroupItemVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-inter font-medium text-neutral-600 ring-offset-white transition-[background-color,color,box-shadow,font-weight] duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_i]:leading-none [&_i]:text-neutral-600 [&_svg]:shrink-0 [&_svg]:text-neutral-600 data-[state=on]:font-semibold data-[state=on]:[&_i]:text-blue-500 data-[state=on]:[&_svg]:text-blue-500',
  {
    variants: {
      size: {
        md: 'h-8 text-sm leading-5 [&_i]:text-[20px] [&_svg]:size-5',
        sm: 'h-6 text-xs leading-4 [&_i]:text-xs [&_svg]:size-3',
      },
      appearance: {
        // selected chip: white bg, dark-grey text, subtle shadow
        segmented:
          'rounded-lg px-3 data-[state=on]:bg-white data-[state=on]:text-neutral-800 data-[state=on]:shadow-[0_1px_2px_rgba(16,24,40,0.05)]',
        // MenuItem treatment: white selected chip + shadow, near-black text
        nav: 'rounded-lg px-3 data-[state=on]:bg-white data-[state=on]:text-neutral-900 data-[state=on]:shadow-[0_1px_2px_rgba(16,24,40,0.05)]',
      },
    },
    defaultVariants: {
      size: 'md',
      appearance: 'segmented',
    },
  }
)

type ToggleGroupSize = NonNullable<
  VariantProps<typeof toggleGroupVariants>['size']
>
type ToggleGroupAppearance = NonNullable<
  VariantProps<typeof toggleGroupVariants>['appearance']
>

const ToggleGroupVariantContext = React.createContext<{
  size: ToggleGroupSize
  appearance: ToggleGroupAppearance
}>({ size: 'md', appearance: 'segmented' })

type ToggleGroupProps = React.ComponentPropsWithoutRef<
  typeof ToggleGroupPrimitive.Root
> &
  VariantProps<typeof toggleGroupVariants>

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  ToggleGroupProps
>(
  (
    { className, size = 'md', appearance = 'segmented', children, ...props },
    ref
  ) => (
    <ToggleGroupPrimitive.Root
      ref={ref}
      className={cn(toggleGroupVariants({ size, appearance }), className)}
      {...props}
    >
      <ToggleGroupVariantContext.Provider
        value={{ size: size ?? 'md', appearance: appearance ?? 'segmented' }}
      >
        {children}
      </ToggleGroupVariantContext.Provider>
    </ToggleGroupPrimitive.Root>
  )
)
ToggleGroup.displayName = 'ToggleGroup'

type ToggleGroupItemProps = React.ComponentPropsWithoutRef<
  typeof ToggleGroupPrimitive.Item
> &
  VariantProps<typeof toggleGroupItemVariants>

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  ToggleGroupItemProps
>(({ className, size, appearance, ...props }, ref) => {
  const context = React.useContext(ToggleGroupVariantContext)
  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleGroupItemVariants({
          size: size ?? context.size,
          appearance: appearance ?? context.appearance,
        }),
        className
      )}
      {...props}
    />
  )
})
ToggleGroupItem.displayName = 'ToggleGroupItem'

export { ToggleGroup, ToggleGroupItem }
